// src/context/AuthContext.js
import { createContext, useReducer, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../services/firebase';
import { doc, getDoc, getFirestore, updateDoc, serverTimestamp } from 'firebase/firestore';

export const AuthContext = createContext();

export const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN':
      return { ...state, user: action.payload };
    case 'LOGOUT':
      return { ...state, user: null, userProfile: null };
    case 'AUTH_IS_READY':
      return { ...state, user: action.payload, authIsReady: true };
    case 'SET_USER_PROFILE':
      return { ...state, userProfile: action.payload };
    default:
      return state;
  }
};

export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, { 
    user: null,
    userProfile: null,
    authIsReady: false
  });
  const [profileLoading, setProfileLoading] = useState(false);

  // Fetch user profile from Firestore when auth state changes
  const fetchUserProfile = async (user, retryCount = 0) => {
    if (!user) return;
    
    try {
      setProfileLoading(true);
      const db = getFirestore();
      
      // Get the user profile from Firestore
      const userProfileRef = doc(db, "user_profiles", user.uid);
      const userProfileDoc = await getDoc(userProfileRef);
      
      if (userProfileDoc.exists()) {
        const userData = userProfileDoc.data();
        
        // Check if user is archived - sign them out immediately
        if (userData.isArchived === true || userData.status === 'archived') {
          console.warn(`Archived user ${user.uid} attempted to log in. Signing out.`);
          try {
            await signOut(auth);
            dispatch({ type: 'LOGOUT' });
            // Show error message to user
            alert('Your account has been archived. Please contact support if you believe this is an error.');
            return;
          } catch (signOutError) {
            console.error('Error signing out archived user:', signOutError);
          }
        }
        
        // Auto-update status to "active" for users who:
        // 1. Have "invited" status (transition from invited to active on login)
        // 2. Don't have a status field (legacy users or direct signups)
        // Note: Waitlist users keep their "waitlist" status
        if (userData.status === 'invited' || !userData.status) {
          try {
            console.log(`Auto-updating user ${user.uid} status to "active" (previous: ${userData.status || 'none'})`);
            await updateDoc(userProfileRef, {
              status: 'active',
              statusUpdatedAt: serverTimestamp(),
              lastUpdated: serverTimestamp()
            });
            
            // Update the local state with the new status
            userData.status = 'active';
            userData.statusUpdatedAt = new Date(); // Use local timestamp for immediate UI update
          } catch (updateError) {
            console.error('Error updating user status to active:', updateError);
            // Continue with the original status if update fails
          }
        }
        
        dispatch({ 
          type: 'SET_USER_PROFILE', 
          payload: userData
        });
      } else {
        // Profile doesn't exist yet - this could be a race condition where the profile
        // is being created by a cloud function (e.g., waitlist signup)
        // Retry a few times before defaulting to 'active'
        if (retryCount < 3) {
          console.log(`Profile not found for user ${user.uid}, retrying... (attempt ${retryCount + 1}/3)`);
          // Keep profileLoading true during retries to prevent premature routing decisions
          await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
          return fetchUserProfile(user, retryCount + 1);
        }
        
        console.warn(`No user profile found for user: ${user.uid} after ${retryCount} retries`);
        // Set an empty profile object to prevent blank screens
        // This allows the app to function even if profile is missing
        // Default to 'active' as this typically indicates a direct signup through /auth
        dispatch({ 
          type: 'SET_USER_PROFILE', 
          payload: {
            uid: user.uid,
            userId: user.uid,
            email: user.email,
            displayName: user.displayName,
            status: 'active', // Default to 'active' for direct signups through /auth
            basicInfo: {
              email: user.email,
              name: user.displayName
            }
          }
        });
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      dispatch({ type: 'AUTH_IS_READY', payload: user });
      
      // If user is authenticated, fetch their profile
      // Waitlist users can authenticate (e.g., by logging in later)
      // but routing logic in App.js restricts them to /waitlist only
      if (user) {
        await fetchUserProfile(user);
      }
    });

    // Cleanup subscription on unmount
    return () => unsub();
  }, []);
  
  return (
    <AuthContext.Provider value={{ ...state, profileLoading, dispatch, fetchUserProfile }}>
      {children}
    </AuthContext.Provider>
  );
};